import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PageSEO } from '@/components/base/PageSEO';
import { Breadcrumb, getCategoryName } from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'feature-guide');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const category = categories.find((c) => c.slug === activeCategory);
  const categoryName = category?.name || activeCategory;

  const filtered = articles.filter((a) => a.category === activeCategory);

  const categorySEODescription = `浏览一起草${categoryName}栏目的全部文章，${category?.description || '获取最新最全的' + categoryName + '内容'}`;
  const categoryKeywords = `一起草${categoryName},${categoryName}指南,一起草教程,一起草使用指南`;

  return (
    <>
      <PageSEO
        title={categoryName}
        description={categorySEODescription}
        keywords={categoryKeywords}
        canonicalUrl={`/category/${activeCategory}`}
        schema={[{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${categoryName} - 一起草`,
          description: categorySEODescription,
          url: `https://jlsjhjt.cn/category/${activeCategory}`,
        }]}
      />

      <main className="pt-20 md:pt-24 pb-16 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryName },
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500 max-w-lg">
              {category?.description}
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mb-10">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-background-300/60 transition-all duration-200 group cursor-pointer"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span className="flex items-center gap-1">
                        <i className="ri-calendar-line"></i> {article.date}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {article.readTime}
                      </span>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 bg-secondary-50 text-secondary-700 text-xs rounded-md"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-background-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}